import { useEffect, useState } from "react";
import axios from "axios";
import { motion } from "framer-motion";
import { ArrowLeft, CalendarCheck } from "lucide-react";
import { toast } from "sonner";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const pad = (n) => String(n).padStart(2, "0");
const toKey = (d) => `${d.getFullYear()}-${pad(d.getMonth() + 1)}-${pad(d.getDate())}`;

const buildDays = (count = 12) => {
  const days = [];
  const d = new Date();
  while (days.length < count) {
    d.setDate(d.getDate() + 1);
    if (d.getDay() !== 0) days.push(new Date(d)); // closed Sundays
  }
  return days;
};

const prettyDate = (key) =>
  new Date(`${key}T12:00:00`).toLocaleDateString("en-US", {
    weekday: "long",
    month: "long",
    day: "numeric",
  });

export const BookingPicker = ({ client, onDone }) => {
  const [days] = useState(buildDays);
  const [date, setDate] = useState(toKey(days[0]));
  const [slots, setSlots] = useState([]);
  const [time, setTime] = useState(null);
  const [loading, setLoading] = useState(false);
  const [booking, setBooking] = useState(null);

  useEffect(() => {
    const load = async () => {
      setLoading(true);
      setTime(null);
      try {
        const res = await axios.get(`${API}/availability`, { params: { date } });
        setSlots(res.data.slots);
      } catch {
        setSlots([]);
      } finally {
        setLoading(false);
      }
    };
    load();
  }, [date]);

  const confirm = async () => {
    if (!time) return;
    setLoading(true);
    try {
      const res = await axios.post(`${API}/bookings`, {
        name: client.name,
        email: client.email,
        phone: client.phone,
        service: client.service,
        date,
        time,
      });
      setBooking(res.data);
      toast.success("Consultation booked", {
        description: `${prettyDate(res.data.date)} at ${res.data.time} ET`,
      });
    } catch (err) {
      if (err?.response?.status === 409) {
        toast.error("That slot was just taken", { description: "Please pick another time." });
        const res = await axios.get(`${API}/availability`, { params: { date } });
        setSlots(res.data.slots);
        setTime(null);
      } else {
        toast.error("Booking failed", { description: "Please call (770) 912-1516 to book." });
      }
    } finally {
      setLoading(false);
    }
  };

  if (booking) {
    return (
      <motion.div
        initial={{ opacity: 0, y: 20 }}
        animate={{ opacity: 1, y: 0 }}
        transition={{ duration: 0.7, ease: [0.22, 1, 0.36, 1] }}
        className="gold-border-glow flex h-full flex-col items-center justify-center bg-wine/70 p-10 text-center"
        data-testid="booking-confirmation"
      >
        <span className="flex h-16 w-16 items-center justify-center border border-gold/40 bg-gold/15 text-gold">
          <CalendarCheck size={28} strokeWidth={1.5} />
        </span>
        <h3 className="mt-8 font-display text-2xl font-semibold text-white sm:text-3xl">
          You’re booked.
        </h3>
        <p className="mt-4 font-mono text-xs uppercase tracking-[0.25em] text-gold" data-testid="booking-confirmation-slot">
          {prettyDate(booking.date)} · {booking.time} ET
        </p>
        <p className="mt-6 max-w-sm text-sm leading-relaxed text-white/55">
          A confirmation email is on its way to {booking.email}. Roberta will meet you at your
          scheduled time — bring any questions about {booking.service.toLowerCase()}.
        </p>
        <button
          onClick={onDone}
          data-testid="booking-done-button"
          className="mt-10 border border-white/20 px-8 py-3.5 font-mono text-xs uppercase tracking-[0.2em] text-white/80 transition-all duration-300 hover:border-gold/60 hover:text-gold"
        >
          Done
        </button>
      </motion.div>
    );
  }

  return (
    <motion.div
      initial={{ opacity: 0, y: 20 }}
      animate={{ opacity: 1, y: 0 }}
      transition={{ duration: 0.7, ease: [0.22, 1, 0.36, 1] }}
      className="gold-border-glow bg-wine/70 p-8 sm:p-10"
      data-testid="booking-picker"
    >
      <p className="font-mono text-[10px] uppercase tracking-[0.3em] text-gold" data-testid="booking-eyebrow">
        Request received — now pick your time
      </p>
      <h3 className="mt-4 font-display text-xl font-semibold text-white sm:text-2xl">
        Choose your consultation slot, {client.name.split(" ")[0]}.
      </h3>
      <p className="mt-3 text-sm text-white/50">All times Eastern · Mon–Sat · 45 minutes</p>

      <div className="mt-8">
        <p className="mb-3 font-mono text-[10px] uppercase tracking-[0.25em] text-white/50">Select a day</p>
        <div className="flex gap-2 overflow-x-auto pb-2" data-testid="booking-day-list">
          {days.map((d) => {
            const key = toKey(d);
            const active = key === date;
            return (
              <button
                key={key}
                onClick={() => setDate(key)}
                data-testid={`booking-day-${key}`}
                className={`flex w-16 shrink-0 flex-col items-center border px-2 py-3 transition-all duration-300 ${
                  active
                    ? "border-gold bg-gold/15 text-gold"
                    : "border-white/15 text-white/50 hover:border-gold/40 hover:text-gold"
                }`}
              >
                <span className="font-mono text-[10px] uppercase tracking-widest">
                  {d.toLocaleDateString("en-US", { weekday: "short" })}
                </span>
                <span className="mt-1 font-display text-lg">{d.getDate()}</span>
                <span className="font-mono text-[9px] uppercase tracking-widest opacity-60">
                  {d.toLocaleDateString("en-US", { month: "short" })}
                </span>
              </button>
            );
          })}
        </div>
      </div>

      <div className="mt-8">
        <p className="mb-3 font-mono text-[10px] uppercase tracking-[0.25em] text-white/50">
          Available times · {prettyDate(date)}
        </p>
        {slots.length === 0 && !loading ? (
          <p className="text-sm text-white/40" data-testid="booking-no-slots">No availability this day — pick another.</p>
        ) : (
          <div className="grid grid-cols-2 gap-2 sm:grid-cols-4" data-testid="booking-slot-list">
            {slots.map((s) => (
              <button
                key={s.time}
                disabled={!s.available}
                onClick={() => setTime(s.time)}
                data-testid={`booking-slot-${s.time.replace(/[^a-z0-9]+/gi, "-").toLowerCase()}`}
                className={`border px-3 py-3 font-mono text-xs tracking-widest transition-all duration-300 ${
                  !s.available
                    ? "cursor-not-allowed border-white/5 text-white/20 line-through"
                    : time === s.time
                      ? "border-gold bg-gold text-obsidian"
                      : "border-white/15 text-white/60 hover:border-gold/40 hover:text-gold"
                }`}
              >
                {s.time}
              </button>
            ))}
          </div>
        )}
      </div>

      <div className="mt-10 flex flex-wrap items-center gap-4">
        <button
          onClick={confirm}
          disabled={!time || loading}
          data-testid="booking-confirm-button"
          className="bg-gold px-8 py-4 font-mono text-xs uppercase tracking-[0.25em] text-obsidian transition-all duration-300 hover:bg-gold-light disabled:cursor-not-allowed disabled:opacity-40"
        >
          {loading ? "Confirming…" : time ? `Confirm ${time}` : "Select a time"}
        </button>
        <button
          onClick={onDone}
          data-testid="booking-skip-button"
          className="flex items-center gap-2 font-mono text-[11px] uppercase tracking-[0.2em] text-white/40 transition-colors duration-300 hover:text-gold"
        >
          <ArrowLeft size={13} /> Skip — Roberta will contact me
        </button>
      </div>
    </motion.div>
  );
};
